import { ReactNode, useEffect, useRef } from 'react';
import { useAuthStore } from '@store/useAuthStore';
import { adminsControllerGetMyProfileV1, adminsControllerRefreshTokenV1 } from '@api/generated/admin-auth/admin-auth';

type AuthInitializerProps = {
    children: ReactNode;
}

// 앱 초기화 시 인증 상태를 검증 및 토큰을 갱신하는 컴포넌트
const AuthInitializer = ({ children }: AuthInitializerProps) => {
    const { isAuthenticated, accessToken, admin, setAccessToken, setAdmin, logout, setIsLoading } = useAuthStore();
    const isInitialized = useRef(false);

    useEffect(() => {
        // 이미 초기화된 경우 스킵
        if (isInitialized.current) return;

	    const initializeAuth = async () => {
		    // 1. 인증되지 않았거나, accessToken과 admin 정보가 모두 있으면 초기화 불필요
		    if (!isAuthenticated || (accessToken && admin)) {
			    setIsLoading(false);
			    isInitialized.current = true;
			    return;
		    }

		    // 2. 인증되어 있지만 accessToken 또는 admin 정보가 없는 경우 재조회
		    setIsLoading(true);
		    try {
			    // accessToken 갱신 (없는 경우)
			    if (!accessToken) {
				    const tokenResponse = await adminsControllerRefreshTokenV1();
				    setAccessToken(tokenResponse.accessToken);
			    }

			    // 관리자 정보 조회 (없는 경우)
			    if (!admin) {
				    const adminResponse = await adminsControllerGetMyProfileV1();
				    setAdmin(adminResponse);
			    }
		    } catch {
			    // 갱신 실패 또는 만료 시 로그아웃
			    logout();
		    } finally {
			    setIsLoading(false);
			    isInitialized.current = true;
		    }
	    };

        initializeAuth();
    }, [isAuthenticated, accessToken, admin, setAccessToken, setAdmin, logout, setIsLoading]);

    return children;
};

export default AuthInitializer;
